import React, { ReactNode, useEffect, useRef, useState } from 'react';
import produce from 'immer';
import cn from 'classnames';
import './Locations.scss';
import { ErrorMessage, Loader, Page, PageHeader } from 'components';
import { DeviceLoadState, ErrorType, LoadState } from 'enums';
import { ILocation } from 'interfaces';
import { LocationItem } from './LocationItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalBody, SearchDropdown, Button, Tooltip, Modal, ModalFooter, ModalHeader, TextField, ButtonGroup } from "@cfa/react-components";
import { MruService, PingService, LocationsService } from 'services';
import { Overlay, Popover } from 'react-bootstrap';

interface ILocationsPageState {
    // location list
    locationLoadStatus: DeviceLoadState;
    locationLoadError?: string;
    // select
    selectedLocations?: ILocation[];
    // location modal
    // TODO

    offset: number
}

export function Locations() {

    const [pageState, setPageState] = useState<ILocationsPageState>({
        locationLoadStatus: DeviceLoadState.Initial,
        selectedLocations: [],
        offset: 0,
    });
    const { locationLoadStatus, locationLoadError, selectedLocations, offset } = pageState;
    const [locations, setLocations] = useState<ILocation[]>(LocationsService.locationsPageCache);

    // maintain locations cache for navigating to a different component
    useEffect(() => {
        LocationsService.locationsPageCache = locations;
    }, [locations])

    // search button and popover
    const searchButtonRef = useRef(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchValue, setSearchValue] = useState<{ number: string, name: string }>()

    // mru button and popover
    const mruButtonRef = useRef(null);
    const [mruOpen, setMruOpen] = useState(false);

    // select all
    const [allSelected, setAllSelected] = useState(false);

    // ping
    const [pingDisabled, setPingDisabled] = useState(false);
    
    const prependLocations = (newLocations: ILocation[]) => {
        setLocations((prevLocations) => {
            return [...newLocations, ...prevLocations];
        })
    }

    // Location select
    const isSelected = input => selectedLocations.some(({ number }) => number == input);

    const onToggleSelectAll = () => {
        let newSelectedLocations;

        if (allSelected) newSelectedLocations = [];
        else newSelectedLocations = locations.slice();

        setAllSelected(!allSelected);
        setPageState(produce(draft => { draft.selectedLocations = newSelectedLocations }))
    }

    const onSelectLocation = (l: ILocation) => {
        let newSelectedLocations = selectedLocations.slice();

        if (isSelected(l.number)) {
            const deviceIndex = newSelectedLocations.findIndex(({ number }) => number == l.number);
            newSelectedLocations.splice(deviceIndex, 1);
        }
        else newSelectedLocations.push(l);

        setPageState(produce(draft => { draft.selectedLocations = newSelectedLocations }))
    }

    const addSearchedLocation = (value) => {
        if (!value) return;

        if (locations.find(l => l.number == value.number)) return;

        prependLocations([{
            number: value.number, name: value.name,
            pingStatus: LoadState.Initial
        }]);
    }

    const setLocationStates = (locations: ILocation[], locationsToChange: ILocation[], state: LoadState) => {
        if (!locationsToChange) return;

        locations = locations.map(l => {
            if (locationsToChange.find(d => d.number == l.number)) {
                return { ...l, pingStatus: state };
            }
            return l;
        })

        return locations;
    }

    const pingLocations = (locationsToPing: ILocation[]) => {
        if (locationsToPing.length == 0) return;

        setPingDisabled(true);
        setLocations((locations) => setLocationStates(locations, locationsToPing, LoadState.Loading));

        PingService.pingLocations(locationsToPing)
            .then((resp) => {
                setLocations((locations) => {
                    let newLocations = setLocationStates(locations, resp.success, LoadState.Success);
                    newLocations = setLocationStates(newLocations, resp.fail, LoadState.Error);
                    return newLocations;
                })

                setPingDisabled(false);
            })
            .catch((e) => {
                setLocations((locations) => setLocationStates(locations, locationsToPing, LoadState.Error));
                setPingDisabled(false);
            });
    }

    const locationItemClassName = (number: string) => {
        if (!locations) {
            return "locationsItem";
        }
        let loc = locations.find(d => d.number == number);
        return cn("locationsItem", {
            'pingStatus_loading': loc.pingStatus === LoadState.Loading,
            'pingStatus_success': loc.pingStatus === LoadState.Success,
            'pingStatus_error': loc.pingStatus === LoadState.Error,
        });

    }

    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkLocationSearch, setBulkLocationSearch] = useState("");
    const searchModal =
        <Modal
            disableOverlayClick
            show={showBulkModal}
            onClose={() => setShowBulkModal(false)}
        >
            <ModalHeader>Bulk Location Search</ModalHeader>
            <ModalBody>
                <TextField multiline={true} rows={10} fullWidth
                    placeholder="Space or comma separated list of location numbers" 
                    onChange={(e) => setBulkLocationSearch(e.target.value)}>
                </TextField>
            </ModalBody>
            <ModalFooter>
                <Button
                    onClick={(e) => {
                        setShowBulkModal(false);
                        let newLocations = bulkLocationSearch.split(/\s*[,\s]\s*/);
                        LocationsService.getLocations(newLocations).then((locs) => {
                            setLocations(locs);
                        })
                    }}
                >
                    Search
                </Button>
            </ModalFooter>
        </Modal>

    const searchHeader: ReactNode = (
        <div className="locationSearch">
            <ButtonGroup size='md'>

                <Button onClick={() => setSearchOpen(!searchOpen)} ref={searchButtonRef}>
                    <FontAwesomeIcon icon="search"/>
                </Button>

                <Overlay
                    target={searchButtonRef}
                    show={searchOpen}
                    onHide={() => setSearchOpen(false)}
                    rootClose
                    placement="bottom"
                >
                    <Popover id="locationSearch_popover" className="searchPopover">
                        <Popover.Content>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Locations'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <SearchDropdown
                                            onSearch={async (query, offset) => {
                                                const resultsPromise = LocationsService.searchLocations(query)
                                                return resultsPromise.then((results) => {
                                                    return ({ canLoadMore: false, results: results })
                                                })
                                            }}
                                            value={searchValue}
                                            onChange={(newValue) => {
                                                addSearchedLocation(newValue)
                                            }}
                                            renderOption={(option) => {
                                                return {
                                                    id: option.number,
                                                    isHidden: false,
                                                    textValue: option.number,
                                                    rendered: <div>{option.number} - {option.name}</div>
                                                };
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Popover.Content>
                    </Popover>
                </Overlay>

                <Button onClick={() => setShowBulkModal(true)}>
                    <FontAwesomeIcon icon="search-plus"/>
                </Button>

                <Button onClick={() => setMruOpen(!mruOpen)} ref={mruButtonRef}>
                    <FontAwesomeIcon icon="history"/>
                </Button>

                <Overlay
                    target={mruButtonRef}
                    show={mruOpen}
                    onHide={() => setMruOpen(false)}
                    rootClose
                    placement="bottom"
                >
                    <Popover id="locationMru_popover" className="searchPopover">
                        <Popover.Content>
                            {MruService.mruLocations.length == 0 && "No MRU locations found"}
                            {MruService.mruLocations.map(l => (
                                <div key={l.uuid} className="mruContainer">
                                    {l.lastRequested > 0 ? new Date(l.lastRequested*1000).toLocaleString() : ""}
                                    <button style={{float: 'right'}}>
                                        <FontAwesomeIcon icon="search" />
                                    </button>
                                    {l.locations.slice(0, Math.min(3, l.locations.length)).map((name, i) => (
                                        <div key={i}>
                                            - {name}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </Popover.Content>
                    </Popover>
                </Overlay>

                <Button onClick={() => setLocations([])} disabled={locations.length == 0}>
                    <FontAwesomeIcon icon="window-close"/>
                </Button>

                <Button onClick={() => pingLocations(selectedLocations)} disabled={selectedLocations.length == 0 || pingDisabled}>
                    Ping
                </Button>
            </ButtonGroup>
        </div>
    );

    const tableHeaders = (locations.length > 0 && (
        <div className="locationsHeaders">
            <div className="locationsHeaders_sorter locationsHeader">
                <input
                    type="checkbox"
                    className="locationsItem_check locationsHeaders_selectAll"
                    onChange={onToggleSelectAll}
                    checked={allSelected}
                />
                <div className="locationsHeaders_selectAllText">
                    Select All
                </div>
            </div>
        </div>)
    );

    let content: ReactNode;
    switch (locationLoadStatus) {
        case DeviceLoadState.Initial:
        case DeviceLoadState.Loading:
        case DeviceLoadState.Success:
            if (locations.length > 0 || locationLoadStatus === DeviceLoadState.Loading) {
                content = (<>
                    <ul className="locationsList">
                        {locations.map((location) => (
                            <LocationItem
                                key={location.number}
                                location={location}
                                isChecked={isSelected(location.number)}
                                className={locationItemClassName(location.number)}
                                onSelectLocation={() => onSelectLocation(location)}
                                onPingLocation={pingLocations}
                            />
                        ))}
                    </ul>
                    {locationLoadStatus === DeviceLoadState.Loading && (
                        <Loader />
                    )}
                </>);
            } else {
                content = (
                    <div className="locationsList_empty">
                        {'No locations found.'}
                    </div>
                );
            }
            break;
        case DeviceLoadState.Error:
            content = (
                <ErrorMessage type={ErrorType.Generic} message={locationLoadError || 'Error loading locations.'} />
            );
            break;
    }

    return (<>
        <Page>
            <PageHeader title={locations.length === 0 ? 'Locations' : 'Locations (' + locations.length + ')'} right={searchHeader} />
            {tableHeaders}
            {content}
        </Page>
        {searchModal}
    </>);
}
